<script setup lang="ts">
  import { ref, onMounted, watch, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useUserStore } from '@/shared/store'
  import { type Country, CountryBoundType } from '@/shared/types/api.types'
  import {
    useValidation,
    useValidators,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import {
    DEFAULT_TITLE_KEY,
    DEFAULT_VALUE_KEY,
    DESTINATION_PRIORITY_IDS,
    LOAD_EVENT_NAME,
    PROCEED_EVENT_NAME,
    DESTINATION_MASK_CONFIG,
  } from './FlowDestinationWidgetQC.constants'
  import { useFindCountries } from '@/shared/composables/useFindCountries'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import SelectQC from '@qicgroupdoha/qic-connect-ds/components/SelectQC'
  import CountryFlagQC from '@qicgroupdoha/qic-connect-ds/components/CountryFlagQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { storeToRefs } from 'pinia'

  interface Props {
    loading?: boolean
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean
    ): void
    (e: 'back'): void
  }

  withDefaults(defineProps<Props>(), {
    loading: false,
    withBackButton: false,
  })
  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const route = useRoute()
  const { t, locale } = useI18n()
  const { findCountries, sortCountries } = useFindCountries()
  const { destinationValidator } = useValidators()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const gaService = useGoogleAnalytics()
  const popularDestinations = [
    {
      id: '997',
      name: t('travel.outbound.popular_destinations.gcc'),
    },
    {
      id: '999',
      name: t('travel.outbound.popular_destinations.worldwide'),
    },
    {
      id: '998',
      name: t('travel.outbound.popular_destinations.schengen'),
    },
    {
      id: '202',
      name: t('travel.outbound.popular_destinations.georgia'),
    },
  ]
  const { destination } = storeToRefs(userStore)
  const selectedOption = ref(destination.value?.map((country) => country?.id) ?? [])
  const isLoading = ref(true)
  const listOfDestionations = ref<Country[]>([])

  const localPopularDestinations = computed(() =>
    popularDestinations.filter((dest) => !selectedOption.value?.includes(dest.id))
  )

  const selectPopularDestination = (id: string) => {
    if (!selectedOption.value?.length) {
      selectedOption.value = [id]
    } else {
      selectedOption.value.push(id)
    }
  }

  const presetDestinationFromUrl = (): boolean => {
    const param = route.query.destinationCode
    if (!param) return false

    const codes = Array.isArray(param) ? param : [param]

    const matchedDestinations = listOfDestionations.value
      .filter((dest) => codes.includes(dest.id))
      .map((dest) => ({
        id: dest.id,
        name: dest.name,
      }))

    if (!matchedDestinations.length) return false

    selectedOption.value = matchedDestinations.map((dest) => dest.id)
    userStore.setDestination(matchedDestinations, true)

    return true
  }

  const validationRules = {
    selectedOption: {
      destinationValidator,
    },
  }

  const { v$, errors } = useValidation(validationRules, { selectedOption })

  const onProceed = () => {
    if (listOfDestionations.value.length === 0) return

    v$.value.$touch()

    if (!v$.value.$error) {
      const formattedDestinations = listOfDestionations.value
        .filter((dest) => selectedOption.value.includes(dest.id))
        .map((dest) => ({
          id: dest.id,
          name: dest.name,
        }))

      userStore.setDestination(formattedDestinations, true)
      gaService.sendDestinationProceed(formattedDestinations.map((dest) => dest.id))
      emit(PROCEED_EVENT_NAME)
    }
  }

  onMounted(async () => {
    await emit(
      LOAD_EVENT_NAME,
      async () => {
        const countries = await userStore.fetchCountries(
          CountryBoundType.OUTBUND,
          locale.value as Locale
        )
        listOfDestionations.value = sortCountries(countries, DESTINATION_PRIORITY_IDS).map(
          (country) => ({
            iso: country.isocode,
            ...country,
          })
        )

        presetDestinationFromUrl()

        onPerformanceCheckEnd()
        gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.DESTINATION)

        isLoading.value = false
      },
      'fetchCountries',
      false
    )
  })

  watch(
    () => selectedOption.value,
    () => userStore.setTravelDates(null, null, true)
  )
</script>

<template>
  <FlowCardQC
    :title="t('outbound_destinations_header')"
    with-button
    :with-back-button="withBackButton"
    :loading="loading"
    @button-click="onProceed"
    @back="emit('back')"
  >
    <template #header>
      <p class="text-regular">
        {{ $t('outbound_destinations_subtitle') }}
      </p>
    </template>

    <WrapperQC direction="column" gap="var(--spacing-4)">
      <SelectQC
        v-model="selectedOption"
        :label="$t('home_form_outbound_destinations_label')"
        :errors="errors.selectedOption"
        is-multi
        name="destination"
        :loading="isLoading || loading"
        :value-key="DEFAULT_VALUE_KEY"
        :title-key="DEFAULT_TITLE_KEY"
        :mask-config="DESTINATION_MASK_CONFIG"
        :filter-function="(search: string) => findCountries(search, listOfDestionations)"
        is-filterable
        :focus-after-clear="false"
        :locale="(locale as Locale) ?? 'en'"
        :options="listOfDestionations"
        :container-attrs="{ 'data-test': 'destination-select' }"
        data-test="destination-select"
      >
        <template #option-before-text="{ option }">
          <CountryFlagQC v-if="option.iso && typeof option.iso === 'string'" :iso="option.iso" />
        </template>
      </SelectQC>

      <WrapperQC
        v-if="localPopularDestinations.length > 0 && listOfDestionations.length > 0"
        class="popular-destinations-wrapper"
        gap="var(--spacing-3)"
      >
        <TransitionGroup name="list">
          <WrapperQC
            v-for="option in localPopularDestinations"
            :key="option.id"
            padding="var(--spacing-2) var(--spacing-3)"
            class="text-medium popular-destination-option"
            @click="selectPopularDestination(option.id)"
          >
            <WrapperQC padding="var(--spacing-0) var(--spacing-2)">
              {{ option.name }}
            </WrapperQC>
          </WrapperQC>
        </TransitionGroup>
      </WrapperQC>
    </WrapperQC>
  </FlowCardQC>
</template>

<style scoped lang="scss">
  .popular-destinations-wrapper {
    position: relative;
    flex-wrap: wrap;
  }

  .popular-destination-option {
    border-radius: var(--radius-3);
    border: 1px solid var(--colorBrdrSrfSecondary);
    background-color: var(--colorBgSrfPrimary);
    color: var(--colorTxtSrfSecondary);
    transition: background-color 0.15s ease;
    cursor: pointer;

    &:hover {
      background-color: var(--colorBgSrfTertiary);
    }
  }

  .list-move,
  .list-enter-active,
  .list-leave-active {
    transition: all 0.5s ease;
  }

  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    transform: translateX(-50px);
  }

  .list-leave-active {
    position: absolute;
  }
</style>
